import { AppRoutes } from '@/constants/routes';
import { Permissions } from '@/constants/permissions';
import type { Guide } from './types';

/**
 * The cheque process, end to end.
 *
 * Written for someone who has never used the system: what each screen is FOR, what happens when
 * they press the button, and what the refusals mean. Every screen reference carries the permission
 * needed to open it, so a reader only sees links they can actually follow.
 */

export const chequeGuides: Guide[] = [
  // ── 1. Overview ────────────────────────────────────────────────────────────
  {
    slug: 'cheque-process-overview',
    title: 'How cheques work, end to end',
    section: 'cheques',
    level: 'Basics',
    summary:
      'The whole journey in one page: a blank pad becomes a registered book, a payment claims a number, '
      + 'the cheque is printed, handed over, and finally clears the bank.',
    menuPath: 'Banking & Cash',
    screens: [
      { label: 'Cheque Books',   route: AppRoutes.CHEQUE_BOOK_SETUP,       permission: Permissions.CHEQUE_BOOK_VIEW },
      { label: 'Cheque Layouts', route: AppRoutes.CHEQUE_LAYOUT_SETUP,     permission: Permissions.CHEQUE_LAYOUT_VIEW },
      { label: 'Cheque Register',route: AppRoutes.BANK_CHEQUES,            permission: Permissions.BNK_CHEQUE_VIEW },
      { label: 'Print Queue',    route: AppRoutes.BANK_CHEQUE_PRINT_QUEUE, permission: Permissions.BNK_CHEQUE_PRINT },
    ],
    blocks: [
      { kind: 'callout', tone: 'tip', title: 'Read this one first',
        text: 'Every other cheque guide is a close-up of one stage below. If you only read one page, read this.' },

      { kind: 'para', text:
        'A cheque is a physical object with a number printed on it, and the system’s job is to be able to '
        + 'account for every one of those numbers — the ones you wrote, the ones you spoiled, and the ones '
        + 'still in the drawer. Everything below follows from that.' },

      { kind: 'heading', text: 'The seven stages' },
      { kind: 'steps', steps: [
        { title: 'Register the cheque book',
          where: 'Setup → Cheque Books',
          detail:
            'Enter the range printed on the pad the bank gave you. The system creates one “leaf” per number, '
            + 'each starting as available. Until a bank account has a book, it is unmanaged and any cheque '
            + 'number is accepted — registering the first book is what turns the control on.' },
        { title: 'Point a layout at the bank',
          where: 'Setup → Cheque Layouts',
          detail:
            'A layout says where the ink goes: the millimetre position of the payee line, the amount, the date. '
            + 'Every bank prints its stationery slightly differently, so a layout belongs to a bank — or to one '
            + 'specific account, or to nothing at all, which makes it the fallback for everyone else.' },
        { title: 'Raise the payment voucher',
          where: 'Accounts Payable → Payments',
          detail:
            'Choose the supplier, set the payment type to Cheque, pick the bank account, and give the cheque its '
            + 'number. Saving the draft reserves that leaf — from that moment no other document can claim it.' },
        { title: 'Submit and approve',
          where: 'Accounts Payable → Payments → Workflow',
          detail:
            'Ordinary maker-checker. Whether the person who raised it may also approve it is a company setting.' },
        { title: 'Post it',
          where: 'Accounts Payable → Payments → Post',
          detail:
            'Three things happen together: the ledger entry is written, the cheque appears on the Cheque Register, '
            + 'and the leaf is marked used. A posting that cannot do all three does none of them.' },
        { title: 'Print it',
          where: 'Banking & Cash → Cheque Print Queue',
          detail:
            'Preview the cheque, load the stationery, print, then confirm. Confirming is what records that the '
            + 'cheque physically exists — not pressing Print.' },
        { title: 'Hand it over, then watch it clear',
          where: 'Banking & Cash → Cheque Issuance',
          detail:
            'Record who collected the cheque and when. After that it lives on the register: presented, then '
            + 'cleared when bank reconciliation matches it off the statement — or returned if it bounces.' },
      ] },

      { kind: 'heading', text: 'Two things have a status, and they are not the same' },
      { kind: 'para', text:
        'This is the single most common source of confusion. A LEAF is a number you physically hold. A CHEQUE is '
        + 'an instrument you have written. They have separate lifecycles and separate screens.' },
      { kind: 'table',
        headers: ['', 'The leaf (Cheque Books)', 'The cheque (Cheque Register)'],
        rows: [
          ['What it is', 'A number in a pad you own',        'A written instrument'],
          ['Starts as',  'Available',                        'Issued (created when the payment posts)'],
          ['Then',       'Reserved → Used',                  'Presented → Cleared'],
          ['Or',         'Void (spoiled, torn, destroyed)',  'Returned, Cancelled, Stopped'],
          ['Answers',    '“Where is cheque 445603?”',        '“Did that payment clear?”'],
        ] },
      { kind: 'callout', tone: 'note', title: 'Why the leaf exists at all',
        text:
          'A cheque that jammed in the printer and was torn up never reaches the register — nothing was issued. '
          + 'But the number is gone, and an auditor will ask about it. The leaf is where that answer lives.' },
    ],
    related: ['cheque-books', 'cheque-layouts', 'cheque-payment', 'cheque-printing', 'cheque-issuance'],
  },

  // ── 2. Cheque books ────────────────────────────────────────────────────────
  {
    slug: 'cheque-books',
    title: 'Registering a cheque book',
    section: 'cheques',
    level: 'Basics',
    summary:
      'Record the pad the bank gave you so every number in it can be accounted for — and choose whether '
      + 'numbers are taken automatically or typed by hand.',
    menuPath: 'Setup',
    screens: [
      { label: 'Cheque Books', route: AppRoutes.CHEQUE_BOOK_SETUP, permission: Permissions.CHEQUE_BOOK_VIEW },
      { label: 'Company setup', route: AppRoutes.ADMIN_COMPANY,    permission: Permissions.ADMIN_COMPANY_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'A cheque book is your claim that you hold a specific run of numbers on a specific bank account. '
        + 'Registering one creates a leaf for every number in the range, so the system can later tell you what '
        + 'became of each.' },

      { kind: 'heading', text: 'Registering a book' },
      { kind: 'steps', steps: [
        { title: 'Open Cheque Books and press Register book',
          where: 'Setup → Cheque Books' },
        { title: 'Choose the bank account',
          detail: 'The account this pad belongs to. A book always belongs to exactly one account.' },
        { title: 'Give the book a code',
          detail: 'Your own reference, e.g. CB-2026-01. It has to be unique for that bank account.' },
        { title: 'Enter the range printed on the pad',
          detail:
            'First and last cheque number, the number of digits, and a prefix if the bank uses one. The screen '
            + 'tells you how many leaves this will create — check that against the pad in your hand before saving.' },
        { title: 'Save',
          detail: 'Every number in the range becomes an available leaf.' },
      ] },

      { kind: 'callout', tone: 'warn', title: 'Overlapping ranges are refused',
        text:
          'If another book on the same account already holds one of these numbers, the save is refused and names '
          + 'the clashing number and book. Two books claiming the same cheque would make the register unable to '
          + 'say which one you actually hold.' },

      { kind: 'heading', text: 'Automatic or manual numbering' },
      { kind: 'para', text:
        'Company setup carries a Cheque Numbering setting that decides where the number on a payment comes from. '
        + 'Find it under Administration → Company → Finance.' },
      { kind: 'table',
        headers: ['Mode', 'What the payment screen does'],
        rows: [
          ['Manual (default)',
           'You type the number. The book’s format and range are shown beside the field, and a number that is '
           + 'used, voided, held, the wrong shape or out of range is refused.'],
          ['Automatic',
           'You pick the cheque book and the next available number fills in and locks. Nobody reads a number '
           + 'off the pad.'],
        ] },
      { kind: 'callout', tone: 'note', title: 'Accounts with no book are unaffected',
        text:
          'Neither mode applies to a bank account nobody has registered a book for. There is nothing to take a '
          + 'number from, so the field stays free text — exactly as it behaved before books existed.' },

      { kind: 'heading', text: 'Voiding a leaf, and closing a book' },
      { kind: 'list', items: [
        'Void a leaf when a cheque is spoiled, torn or destroyed. A reason is required — that reason IS the '
        + 'explanation for the gap in the sequence.',
        'Close a book when the account is closed or the pad is lost. Unused leaves are voided with the reason, '
        + 'so the system cannot later offer a number from a book you no longer have.',
        'Release a leaf when a draft voucher is holding a number nobody intends to use. Abandoned drafts are '
        + 'also swept automatically.',
      ] },
    ],
    related: ['cheque-process-overview', 'cheque-payment'],
  },

  // ── 3. Layouts ─────────────────────────────────────────────────────────────
  {
    slug: 'cheque-layouts',
    title: 'Cheque layouts and assigning them to a bank',
    section: 'cheques',
    level: 'Advanced',
    summary:
      'Set the print coordinates for a bank’s stationery, preview them against a sample cheque, and control '
      + 'which accounts use which layout.',
    menuPath: 'Setup',
    screens: [
      { label: 'Cheque Layouts', route: AppRoutes.CHEQUE_LAYOUT_SETUP, permission: Permissions.CHEQUE_LAYOUT_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'A cheque layout is a map of one bank’s stationery: how big the paper is, and where on it each field is '
        + 'printed. Bank of Ceylon puts its payee line a few millimetres from where Commercial Bank puts its own, '
        + 'which is the whole reason layouts exist.' },

      { kind: 'heading', text: 'Where a layout is attached to a bank' },
      { kind: 'para', text:
        'There is no separate “assign template” screen. The relationship is stored ON the layout: open the layout '
        + 'and set its Bank, and optionally its Bank Account. Both are searchable pickers, and choosing an account '
        + 'fills in its bank for you.' },
      { kind: 'table',
        headers: ['What you set', 'What it means'],
        rows: [
          ['Bank and Bank Account both blank', 'The house default — used by any account whose bank has no layout.'],
          ['Bank set, Account blank',          'This bank’s layout. The usual case.'],
          ['Bank and Account both set',        'Pinned to one account, for stationery that differs from the rest of that bank’s.'],
        ] },
      { kind: 'callout', tone: 'note', title: 'Most specific wins',
        text:
          'When a cheque is printed the system looks for this account’s own layout first, then this bank’s, then '
          + 'the house default. If none exists, printing is refused rather than guessing a position.' },

      { kind: 'heading', text: 'Setting the coordinates' },
      { kind: 'steps', steps: [
        { title: 'Open the layout and go to Print Coordinates',
          detail: 'Every value is in millimetres from the top-left corner of the page.' },
        { title: 'Set the page size first',
          detail: 'Page width and height define the paper. Everything else is positioned inside it.' },
        { title: 'Position each field',
          detail:
            'Payee name, amount in figures, amount in words, cheque date, cheque number. The width fields matter: '
            + 'a field whose position plus width runs past the page edge will be cut off when printed.' },
        { title: 'Add the rubber stamp wording if you want it printed',
          detail:
            'Your company’s authority block, e.g. “FOR ACME (PVT) LTD”. Leave it blank if your stationery is '
            + 'stamped by hand — nothing prints, and the print dialog’s toggle then has nothing to suppress.' },
        { title: 'Check the Preview tab',
          detail:
            'A sample cheque at this layout’s real measurements, with labelled outlines showing where each field '
            + 'lands. Change a coordinate and it moves here. Anything running off the paper is flagged in amber.' },
      ] },
      { kind: 'callout', tone: 'tip', title: 'Always preview before printing a real cheque',
        text:
          'The preview costs nothing. A misplaced coordinate costs a numbered piece of stationery, and getting it '
          + 'back means a reprint — which needs a reason and puts a second cheque on that number.' },
    ],
    related: ['cheque-printing', 'cheque-process-overview'],
  },

  // ── 4. Payment ─────────────────────────────────────────────────────────────
  {
    slug: 'cheque-payment',
    title: 'Paying a supplier by cheque',
    section: 'cheques',
    level: 'Basics',
    summary:
      'Raise the payment voucher, claim a cheque number, get it approved and post it — and what each refusal '
      + 'is telling you.',
    menuPath: 'Accounts Payable',
    screens: [
      { label: 'AP Payments',  route: AppRoutes.AP_PAYMENTS,   permission: Permissions.AP_PAYMENT_VIEW },
      { label: 'New Payment',  route: AppRoutes.AP_PAYMENT_NEW, permission: Permissions.AP_PAYMENT_CREATE },
    ],
    blocks: [
      { kind: 'heading', text: 'Raising the voucher' },
      { kind: 'steps', steps: [
        { title: 'Open a new AP Payment and choose the supplier',
          where: 'Accounts Payable → Payments' },
        { title: 'On the payment line, set Payment Type to Cheque' },
        { title: 'Choose the account under “Pay from”',
          detail:
            'Only accounts this payment may actually draw on are listed. The list is filtered by the payment '
            + 'method, the voucher currency and your location — which is why a USD account does not appear on an '
            + 'LKR payment.' },
        { title: 'Give the cheque its number',
          detail:
            'In manual mode, type it — the book’s format and range are shown beside the field. In automatic mode, '
            + 'pick the cheque book and the next available number fills in and locks.' },
        { title: 'Pick the bills this payment settles',
          where: 'Bills Settled tab',
          detail: 'Leave it empty for an on-account payment. Otherwise tick the bills and the amounts allocate.' },
        { title: 'Save',
          detail: 'The cheque number is now reserved against this voucher. No other document can claim it.' },
      ] },

      { kind: 'heading', text: 'What the refusals mean' },
      { kind: 'table',
        headers: ['Message', 'What happened', 'What to do'],
        rows: [
          ['…has already been issued',
           'That number is on the register already.',
           'Open the Cheque Register to see which document used it, and use the next available number.'],
          ['…was voided',
           'The leaf was written off — spoiled or destroyed.',
           'Use a different number. The void reason explains the gap.'],
          ['…is already reserved by another document',
           'Another draft is holding it.',
           'Use the next available number, or release that document’s hold from Cheque Books.'],
          ['…is not the right shape',
           'The number does not match this account’s stationery — wrong length or missing prefix.',
           'Check the number printed on the cheque; the expected pattern is shown in the message.'],
          ['…is outside every cheque book',
           'The shape is right but no book you hold covers that number.',
           'Register the book it came from, or correct the number.'],
        ] },

      { kind: 'heading', text: 'Approving and posting' },
      { kind: 'para', text:
        'Submit the voucher, have it approved, then post. Posting writes the ledger entry, creates the cheque on '
        + 'the Cheque Register, and marks the leaf used — all in one transaction.' },
      { kind: 'callout', tone: 'note', title: 'Editing a voucher never strands a number',
        text:
          'Changing a cheque number on a draft releases the old leaf and reserves the new one. Cancelling the '
          + 'voucher releases everything it was holding.' },
    ],
    related: ['cheque-books', 'cheque-printing', 'cheque-process-overview'],
  },

  // ── 5. Printing ────────────────────────────────────────────────────────────
  {
    slug: 'cheque-printing',
    title: 'Printing cheques',
    section: 'cheques',
    level: 'Basics',
    summary:
      'Print one cheque or a whole run, preview before the paper is spent, and understand why confirming — not '
      + 'printing — is what the system records.',
    menuPath: 'Banking & Cash',
    screens: [
      { label: 'Cheque Print Queue', route: AppRoutes.BANK_CHEQUE_PRINT_QUEUE, permission: Permissions.BNK_CHEQUE_PRINT },
      { label: 'Cheque Register',    route: AppRoutes.BANK_CHEQUES,            permission: Permissions.BNK_CHEQUE_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'Every cheque you have issued and not yet printed waits in the Print Queue. You can also print the one '
        + 'cheque you are looking at from the Cheque Register. Both work the same way and both record the print.' },

      { kind: 'heading', text: 'Printing a run' },
      { kind: 'steps', steps: [
        { title: 'Open the Cheque Print Queue',
          where: 'Banking & Cash → Cheque Print Queue' },
        { title: 'Tick the cheques you are about to print',
          detail:
            'Only unprinted cheques have a checkbox. Cancelled and already-cleared cheques are not listed at all — '
            + 'the paper has left the building and printing it again would put a second cheque into circulation.' },
        { title: 'Press “Print Selected”' },
        { title: 'Set the options',
          detail:
            'The rubber stamp toggle is on by default; turn it off if your stationery is stamped by hand. The '
            + 'payee shown is what will be printed.' },
        { title: 'Press Preview',
          detail:
            'Each cheque is drawn at the layout’s real measurements. Tick “Show field positions” to see labelled '
            + 'outlines. Anything running off the paper is flagged.' },
        { title: 'Load the stationery and press Print' },
        { title: 'Check the paper, then press Confirm',
          detail:
            'Confirming is what records the cheques as printed. If the run jammed or came out wrong, simply do '
            + 'not confirm — close the dialog and the cheques stay in the queue.' },
      ] },

      { kind: 'callout', tone: 'warn', title: 'A reprint is a different act',
        text:
          'Printing a cheque a second time means two physical cheques could carry the same number. It needs its '
          + 'own permission and a mandatory reason, and it is recorded separately in that cheque’s print history — '
          + 'which is exactly what an auditor reads when the number turns up twice.' },

      { kind: 'heading', text: 'Printing payable to CASH' },
      { kind: 'para', text:
        'The print dialog can override the payee for a run — in practice, to CASH. This is deliberately gated '
        + 'behind its own permission, separate from printing: a cheque payable to CASH can be encashed by whoever '
        + 'holds it, so overriding a named supplier at the moment of printing turns a traceable payment into a '
        + 'bearer instrument. The override never changes the cheque’s recorded payee; it is written to the print '
        + 'log with the user and the value.' },

      { kind: 'heading', text: 'Print history' },
      { kind: 'para', text:
        'Every cheque with a print against it has a History button in the queue: each print and reprint, in order, '
        + 'with who did it, when, and — for reprints — why.' },
    ],
    related: ['cheque-print-run', 'cheque-print-confirm', 'cheque-layouts', 'cheque-issuance'],
  },

  // ── 6. Issuance ────────────────────────────────────────────────────────────
  {
    slug: 'cheque-issuance',
    title: 'Handing a cheque over',
    section: 'cheques',
    level: 'Basics',
    summary:
      'Record who collected the cheque, capture their ID and acknowledgement, and see what is printed but not '
      + 'yet issued.',
    menuPath: 'Banking & Cash',
    screens: [
      { label: 'Cheque Issuance', route: AppRoutes.CHEQUE_ISSUANCES, permission: Permissions.BNK_CHEQUE_ISSUE },
    ],
    blocks: [
      { kind: 'para', text:
        'Printing produces the instrument. Issuance records that it left the building — who took it, when, and '
        + 'on whose authority. The two are separate on purpose: a printed cheque sitting in a drawer is not the '
        + 'same as one in a supplier’s hands.' },

      { kind: 'callout', tone: 'tip', title: 'Where to see printed but not issued',
        text:
          'The count is on the Cheque Issuance header: “N printed and waiting to be collected”. Cheques that have '
          + 'not been printed yet are counted separately — they have no paper to give anyone.' },

      { kind: 'heading', text: 'Recording a hand-over' },
      { kind: 'steps', steps: [
        { title: 'Open Cheque Issuance and press New Issuance',
          where: 'Banking & Cash → Cheque Issuance' },
        { title: 'Choose the cheque',
          detail:
            'The list shows only cheques you have written and not yet handed over, with the payee and amount so '
            + 'you can match the paper in your hand. A cheque marked “not yet printed” should be printed first.' },
        { title: 'Record who is collecting it',
          detail:
            'Collector name, ID type and number, phone, and their relationship to the payee. A witness can be '
            + 'recorded too. This is the evidence that the cheque went to the right person.' },
        { title: 'Save' },
      ] },

      { kind: 'heading', text: 'The three actions afterwards' },
      { kind: 'table',
        headers: ['Action', 'When to use it'],
        rows: [
          ['Acknowledge Receipt', 'The collector has signed for it. Capture the signature reference.'],
          ['Mark Collected',      'The cheque has physically left with them.'],
          ['Cancel',              'The hand-over did not happen. The cheque returns to the waiting list.'],
        ] },
      { kind: 'callout', tone: 'note', title: 'A cancelled hand-over is not a cancelled cheque',
        text:
          'Cancelling an issuance puts the cheque back in the drawer and back on the waiting list. The instrument '
          + 'still exists and is still on the register — to void the cheque itself, use the Cheque Register.' },
    ],
    related: ['cheque-printing', 'cheque-reports', 'cheque-process-overview'],
  },

  // ── 7. Register and reports ────────────────────────────────────────────────
  {
    slug: 'cheque-reports',
    title: 'The cheque register and related reports',
    section: 'cheques',
    level: 'Basics',
    summary:
      'Where to look up any cheque, follow one from voucher to bank statement, and handle a cheque that bounces.',
    menuPath: 'Banking & Cash',
    screens: [
      { label: 'Cheque Register',   route: AppRoutes.BANK_CHEQUES,           permission: Permissions.BNK_CHEQUE_VIEW },
      { label: 'Returned Cheques',  route: AppRoutes.BANK_RETURNED_CHEQUES,  permission: Permissions.BNK_CHEQUE_RETURN },
      { label: 'Cheque Books',      route: AppRoutes.CHEQUE_BOOK_SETUP,      permission: Permissions.CHEQUE_BOOK_VIEW },
    ],
    blocks: [
      { kind: 'heading', text: 'The Cheque Register' },
      { kind: 'para', text:
        'Every cheque the system knows about — the ones you wrote and the ones customers gave you. Each row shows '
        + 'the number, date, bank account, the cheque book it came from, the payee or payer, amount, status and '
        + 'the voucher that created it.' },
      { kind: 'callout', tone: 'note', title: 'A dash in the Book column is correct',
        text:
          'A received cheque is the drawer’s own stationery, not yours, so it belongs to no book of yours. The '
          + 'same is true of a cheque issued from an account whose stock nobody has registered.' },

      { kind: 'heading', text: 'Following one cheque' },
      { kind: 'list', ordered: true, items: [
        'Open the cheque from the register to see its full record and its audit log.',
        'The Related Document is the voucher that created it — that is the link back to the ledger.',
        'Print Voucher produces a printable Cheque Record on your company letterhead, for the file.',
        'The Cheque Books screen shows the same number from the other side: which book it came from and what '
        + 'became of the leaf.',
      ] },

      { kind: 'heading', text: 'When a cheque bounces' },
      { kind: 'para', text:
        'Record it on Returned Cheques. You classify it as representable or not: a representable cheque can be '
        + 'deposited again, a non-representable one is the end of the road. The classification governs the '
        + 'lifecycle only — both post the same reversal.' },

      { kind: 'heading', text: 'Accounting for a missing number' },
      { kind: 'para', text:
        'If someone asks “where is cheque 445603?”, the answer is on Cheque Books, not the register. A leaf is '
        + 'either available (still in the drawer), reserved (a draft is holding it), used (it became a cheque — '
        + 'and links to it), or void (with the reason it was written off).' },
    ],
    related: ['cheque-process-overview', 'cheque-issuance'],
  },
];
